<template>
  <div class="catalog-page">
    <div v-if="loading" class="details-loading">
      <q-spinner-dots color="primary" size="40px" />
    </div>

    <div v-else-if="error" class="details-error">
      <q-banner rounded class="bg-red-1 text-negative">{{ error }}</q-banner>
      <q-btn class="q-mt-md" label="Tentar novamente" icon="refresh" @click="load" />
    </div>

    <template v-else-if="item">
      <section class="gallery-section">
        <div class="gallery-inner">
          <button class="back-btn" @click="router.back()">
            <q-icon name="arrow_back" size="16px" />
            <span>Voltar</span>
          </button>
          <div class="gallery-grid">
            <div class="main-image-wrap">
              <q-img :src="currentImage" :ratio="16 / 10" class="main-image">
                <template #loading><q-spinner-gears color="white" size="40px" /></template>
                <template #error><div class="image-placeholder"><q-icon :name="icon" size="52px" /></div></template>
              </q-img>
              <div class="image-badges">
                <span class="image-badge">{{ categoryLabel }}</span>
                <span class="image-badge image-badge-dark">{{ statusLabel(item.status) }}</span>
              </div>
              <button v-if="images.length > 1" class="nav-button nav-left" aria-label="Imagem anterior" @click="moveImage(-1)">
                <q-icon name="chevron_left" />
              </button>
              <button v-if="images.length > 1" class="nav-button nav-right" aria-label="Próxima imagem" @click="moveImage(1)">
                <q-icon name="chevron_right" />
              </button>
              <div class="photo-count"><q-icon name="photo_library" size="13px" /> {{ images.length ? selectedImage + 1 : 0 }} / {{ images.length }}</div>
            </div>
            <div class="thumbnail-grid">
              <button
                v-for="(image, index) in images.slice(0, 4)"
                :key="image.id || image.url"
                class="thumbnail"
                :class="{ 'thumbnail-active': selectedImage === index }"
                :aria-label="`Ver imagem ${index + 1}`"
                @click="selectedImage = index"
              >
                <q-img :src="image.url" :ratio="1" />
                <span v-if="index === 3 && images.length > 4" class="thumbnail-more">+{{ images.length - 4 }}</span>
              </button>
              <div v-if="!images.length" class="thumbnail-empty"><q-icon :name="icon" size="32px" /></div>
            </div>
          </div>
        </div>
      </section>

      <main class="info-section">
        <div class="info-inner">
          <div class="details-column">
            <header class="title-block">
              <div class="category-label">{{ categoryLabel }}</div>
              <h1 class="listing-title">{{ item.title }}</h1>
              <div class="location-row"><q-icon name="location_on" size="16px" /><span>{{ item.location || "Localização não indicada" }}</span></div>
              <div class="price-block">
                <div class="price-label">{{ kind === "accommodation" ? "Preço por noite" : "Preço diário" }}</div>
                <div class="price-value">{{ mainPrice }}</div>
              </div>
            </header>

            <div class="section-divider" />

            <section class="info-block">
              <h2 class="block-title">Características</h2>
              <div class="feature-grid">
                <div v-for="feature in features" :key="feature.label" class="feature-card">
                  <div class="feature-icon"><q-icon :name="feature.icon" size="20px" /></div>
                  <div class="feature-value">{{ feature.value }}</div>
                  <div class="feature-label">{{ feature.label }}</div>
                </div>
              </div>
            </section>

            <div class="section-divider" />

            <section v-if="kind === 'accommodation' && item.units?.length" class="info-block">
              <h2 class="block-title">Unidades disponíveis</h2>
              <div class="unit-list">
                <article v-for="unit in item.units" :key="unit.id" class="unit-card">
                  <div class="unit-heading">
                    <strong>{{ unit.name || "Unidade" }}</strong>
                    <span>{{ money(unit.price, unit.currency) }} <small>/ noite</small></span>
                  </div>
                  <div class="unit-specs">
                    <span><q-icon name="groups" /> {{ unit.capacity || 1 }} hóspedes</span>
                    <span><q-icon name="bed" /> {{ unit.bedroom_amount || 0 }} quartos</span>
                    <span><q-icon name="bathtub" /> {{ unit.bathroom_amount || 0 }} casas de banho</span>
                  </div>
                  <p v-if="unit.description" class="unit-description">{{ unit.description }}</p>
                </article>
              </div>
            </section>

            <section v-if="kind === 'accommodation' && item.units?.length" class="info-block amenity-block">
              <h2 class="block-title">Comodidades</h2>
              <div class="amenity-list">
                <span v-for="amenity in item.amenities || []" :key="amenity.id" class="amenity-chip">
                  <q-icon name="check_circle" size="15px" /> {{ amenity.name }}
                </span>
                <span v-if="!item.amenities?.length" class="muted">Sem comodidades indicadas.</span>
              </div>
              <p v-if="item.rules" class="rules-text"><strong>Regras:</strong> {{ item.rules }}</p>
            </section>

            <section v-if="kind === 'rental_vehicle' && item.locations?.length" class="info-block amenity-block">
              <h2 class="block-title">Locais de levantamento</h2>
              <div class="amenity-list">
                <span v-for="location in item.locations" :key="location.id" class="amenity-chip">
                  <q-icon name="place" size="15px" /> {{ location.name || location.location || location }}
                </span>
              </div>
            </section>

            <div class="section-divider" />

            <section class="info-block">
              <h2 class="block-title">Descrição</h2>
              <p class="description">{{ item.description || "Sem descrição disponível." }}</p>
            </section>
          </div>

          <aside class="contact-card">
            <div class="agent-row">
              <div class="agent-avatar">
                <img :src="agentPhoto" alt="Fotografia do agente" @error="useAgentFallback" />
              </div>
              <div class="agent-info">
                <div class="agent-name">{{ item.user?.name || "Agente Golden Key" }}</div>
                <div class="agent-role">{{ kind === "accommodation" ? "Anfitrião" : "Agente rent-a-car" }}</div>
              </div>
            </div>

            <div class="card-divider" />

            <div class="contact-actions">
              <a v-if="whatsappUrl" class="contact-button button-whatsapp" :href="whatsappUrl" target="_blank" rel="noopener noreferrer">
                <q-icon name="chat" size="17px" /><span>WhatsApp</span>
              </a>
              <a v-if="item.user?.phone" class="contact-button button-call" :href="`tel:${item.user.phone}`">
                <q-icon name="phone" size="17px" /><span>Ligar agora</span>
              </a>
              <div v-if="!whatsappUrl && !item.user?.phone" class="muted contact-unavailable">Contactos não disponíveis.</div>
            </div>

            <div class="card-divider" />

            <div class="meta-row"><span class="meta-key">Referência</span><span class="meta-value">{{ item.slug }}</span></div>
            <div class="meta-row"><span class="meta-key">Categoria</span><span class="meta-value">{{ categoryLabel }}</span></div>
            <template v-if="kind === 'rental_vehicle'">
              <div class="meta-row"><span class="meta-key">Marca / modelo</span><span class="meta-value">{{ [item.brand, item.model].filter(Boolean).join(" ") || "—" }}</span></div>
              <div class="meta-row"><span class="meta-key">Ano</span><span class="meta-value">{{ item.year || "—" }}</span></div>
            </template>
            <template v-else>
              <div class="meta-row"><span class="meta-key">Unidades</span><span class="meta-value">{{ item.units?.length || 0 }}</span></div>
              <div class="meta-row"><span class="meta-key">Regras</span><span class="meta-value">{{ item.rules || "—" }}</span></div>
            </template>

            <div class="card-divider" />

            <q-btn v-if="canManage" class="sidebar-button button-edit" icon="edit" label="Editar anúncio" unelevated @click="edit" />
            <q-btn v-if="isAdmin && item.status === 'pending_review'" class="sidebar-button button-approve q-mt-sm" icon="verified" label="Aprovar e publicar" unelevated :loading="actionLoading" @click="approve" />
            <q-btn v-if="isAdmin && item.status === 'pending_review'" class="sidebar-button button-reject q-mt-sm" icon="cancel" label="Rejeitar anúncio" unelevated :loading="actionLoading" @click="reject" />
            <q-btn v-if="canManage && canSubmit" class="sidebar-button button-submit q-mt-sm" icon="publish" label="Enviar para validação" unelevated :loading="actionLoading" @click="publish" />
            <q-btn v-if="canManage && item.status === 'published'" class="sidebar-button button-deactivate q-mt-sm" icon="toggle_off" label="Desactivar anúncio" unelevated :loading="actionLoading" @click="deactivate" />

            <div class="card-divider" />
            <div class="status-line"><span>Estado do anúncio</span><q-badge :class="`status-badge status-${item.status}`">{{ statusLabel(item.status) }}</q-badge></div>
            <div v-if="item.rejection_reason" class="rejection-note"><q-icon name="info" size="15px" /> {{ item.rejection_reason }}</div>
          </aside>
        </div>
      </main>
    </template>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import Swal from "sweetalert2";
import apiClient from "@/config/api";

const props = defineProps({ kind: { type: String, required: true } });
const route = useRoute();
const router = useRouter();
const store = useStore();
const item = ref(null);
const loading = ref(true);
const actionLoading = ref(false);
const error = ref("");
const selectedImage = ref(0);
const fallbackAgent = "https://amzn-s3-goldenkey.s3.us-east-2.amazonaws.com/properties/icon2.png";
const agentPhoto = ref(fallbackAgent);
const moduleName = computed(() => props.kind === "accommodation" ? "accommodations" : "rentalVehicles");
const isAdmin = computed(() => Number(store.state.auth.user?.role) === 1);
const canManage = computed(() => isAdmin.value || Number(store.state.auth.user?.id) === Number(item.value?.user?.id));
const canSubmit = computed(() => ["draft", "inactive", "rejected", "expired"].includes(item.value?.status));
const images = computed(() => {
  const listingImages = (item.value?.images || []).filter((image) => image?.url);
  return [...listingImages].sort((left, right) => Number(Boolean(right.is_primary)) - Number(Boolean(left.is_primary)));
});
const currentImage = computed(() => images.value[selectedImage.value]?.url || "");
const icon = computed(() => props.kind === "accommodation" ? "hotel" : "directions_car");
const categoryLabel = computed(() => props.kind === "accommodation" ? item.value?.type?.name || "Alojamento" : item.value?.category?.name || "Rent-a-car");
const activeUnits = computed(() => (item.value?.units || []).filter((unit) => unit.active !== false));
const mainPrice = computed(() => {
  if (props.kind === "accommodation") {
    const unit = [...activeUnits.value].sort((a, b) => Number(a.price || 0) - Number(b.price || 0))[0];
    return unit ? `${money(unit.price, unit.currency)} / noite` : "Preço sob consulta";
  }
  const terms = item.value?.terms;
  return terms?.daily_price != null ? `${money(terms.daily_price, terms.currency)} / dia` : "Preço sob consulta";
});
const features = computed(() => props.kind === "accommodation" ? [
  { icon: "meeting_room", value: activeUnits.value.length, label: "Unidades" },
  { icon: "groups", value: Math.max(0, ...activeUnits.value.map((unit) => Number(unit.capacity || 0))), label: "Hóspedes" },
  { icon: "bed", value: Math.max(0, ...activeUnits.value.map((unit) => Number(unit.bedroom_amount || 0))), label: "Quartos" },
  { icon: "bathtub", value: Math.max(0, ...activeUnits.value.map((unit) => Number(unit.bathroom_amount || 0))), label: "Casas de banho" },
] : [
  { icon: "directions_car", value: [item.value?.brand, item.value?.model].filter(Boolean).join(" ") || "—", label: "Viatura" },
  { icon: "event_seat", value: item.value?.seats || "—", label: "Lugares" },
  { icon: "settings", value: transmissionLabel(item.value?.transmission), label: "Transmissão" },
  { icon: "local_gas_station", value: item.value?.fuel || "—", label: "Combustível" },
]);
const whatsappUrl = computed(() => {
  const number = (item.value?.user?.whatsapp || item.value?.user?.phone || "").replace(/\D/g, "");
  return number ? `https://wa.me/${number}` : "";
});

function money(value, currency) {
  return `${Number(value || 0).toLocaleString("pt-MZ")} ${currency || "MZN"}`;
}
function transmissionLabel(value) {
  return value === "automatic" ? "Automática" : value === "manual" ? "Manual" : value || "—";
}
function statusLabel(status) {
  return { published: "Publicado", active: "Activo", pending_review: "Aguarda validação", draft: "Rascunho", rejected: "Requer correcção", inactive: "Inactivo", expired: "Expirado" }[status] || status || "Estado indisponível";
}
function useAgentFallback() {
  agentPhoto.value = fallbackAgent;
}
function moveImage(direction) {
  selectedImage.value = (selectedImage.value + direction + images.value.length) % images.value.length;
}
async function load() {
  loading.value = true;
  error.value = "";
  try {
    item.value = await store.dispatch(`${moduleName.value}/show`, route.params.slug);
    selectedImage.value = 0;
    agentPhoto.value = item.value.user?.photo || fallbackAgent;
  } catch (requestError) {
    item.value = null;
    error.value = requestError.response?.data?.message || "Não foi possível carregar os detalhes deste anúncio.";
  } finally {
    loading.value = false;
  }
}
function edit() {
  router.push(`${props.kind === "accommodation" ? "/alojamentos" : "/rent-a-car"}/editar/${item.value.slug}`);
}
async function publish() {
  actionLoading.value = true;
  try {
    item.value = await store.dispatch(`${moduleName.value}/publish`, item.value.id);
    Swal.fire({ title: "Enviado para validação", text: "O anúncio aguarda análise do administrador.", icon: "success", timer: 2200, showConfirmButton: false });
  } catch (requestError) {
    Swal.fire("Não foi possível publicar", requestError.response?.data?.message || "Verifique os dados obrigatórios.", "error");
  } finally {
    actionLoading.value = false;
  }
}
async function deactivate() {
  const result = await Swal.fire({ title: "Desactivar anúncio?", text: "O anúncio ficará oculto na plataforma.", icon: "warning", showCancelButton: true, confirmButtonText: "Desactivar", cancelButtonText: "Cancelar" });
  if (!result.isConfirmed) return;
  actionLoading.value = true;
  try {
    item.value = await store.dispatch(`${moduleName.value}/deactivate`, item.value.id);
    Swal.fire({ title: "Anúncio desactivado", icon: "success", timer: 1800, showConfirmButton: false });
  } catch (requestError) {
    Swal.fire("Não foi possível desactivar", requestError.response?.data?.message || "Tente novamente.", "error");
  } finally {
    actionLoading.value = false;
  }
}
async function approve() {
  actionLoading.value = true;
  try {
    const type = props.kind === "accommodation" ? "accommodation" : "rental_vehicle";
    const response = await apiClient.post(`/moderation/${type}/${item.value.id}/approve`);
    item.value = response.data.data;
    await load();
    Swal.fire({ title: "Anúncio aprovado", text: "O anúncio foi publicado.", icon: "success", timer: 2000, showConfirmButton: false });
  } catch (requestError) {
    Swal.fire("Não foi possível aprovar", requestError.response?.data?.message || "Tente novamente.", "error");
  } finally {
    actionLoading.value = false;
  }
}
async function reject() {
  const result = await Swal.fire({
    title: "Rejeitar anúncio",
    input: "textarea",
    inputLabel: "Indique o que o agente deve corrigir",
    inputPlaceholder: "Motivo da rejeição...",
    inputValidator: (value) => value?.trim() ? undefined : "Indique o motivo da rejeição.",
    showCancelButton: true,
    confirmButtonText: "Rejeitar",
    cancelButtonText: "Cancelar",
  });
  if (!result.isConfirmed) return;
  actionLoading.value = true;
  try {
    const type = props.kind === "accommodation" ? "accommodation" : "rental_vehicle";
    await apiClient.post(`/moderation/${type}/${item.value.id}/reject`, { reason: result.value.trim() });
    await load();
    Swal.fire({ title: "Anúncio devolvido para correcção", icon: "success", timer: 2000, showConfirmButton: false });
  } catch (requestError) {
    Swal.fire("Não foi possível rejeitar", requestError.response?.data?.message || "Tente novamente.", "error");
  } finally {
    actionLoading.value = false;
  }
}
onMounted(load);
</script>

<style scoped>
.catalog-page { min-height: 100vh; background: #f4f6fb; }
.details-loading, .details-error { max-width: 1100px; min-height: 55vh; margin: 0 auto; padding: 40px 20px; }
.gallery-section { padding: 22px 20px 18px; background: #fff; }
.gallery-inner, .info-inner { max-width: 1100px; margin: 0 auto; }
.back-btn { display: inline-flex; align-items: center; gap: 7px; margin: 0 0 14px; padding: 7px 11px; border: 1px solid #e8ecf4; border-radius: 9px; background: #fff; color: #334155; font-size: 12px; font-weight: 700; cursor: pointer; }
.gallery-grid { display: grid; grid-template-columns: minmax(0, 2fr) minmax(210px, 1fr); gap: 10px; height: 360px; }
.main-image-wrap { position: relative; min-width: 0; overflow: hidden; border-radius: 13px; background: #e8ecf4; }
.main-image { height: 100%; }
.image-placeholder, .thumbnail-empty { display: grid; width: 100%; height: 100%; min-height: 130px; place-items: center; background: #e8ecf4; color: #94a3b8; }
.image-badges { position: absolute; top: 14px; left: 14px; z-index: 2; display: flex; gap: 7px; }
.image-badge { padding: 5px 10px; border-radius: 14px; background: #d4af37; color: #0d1b2a; font-size: 10px; font-weight: 800; text-transform: uppercase; }
.image-badge-dark { background: rgba(13, 27, 42, .76); color: #fff; }
.nav-button { position: absolute; z-index: 2; top: 50%; display: grid; width: 34px; height: 34px; transform: translateY(-50%); place-items: center; border: 0; border-radius: 50%; background: rgba(13, 27, 42, .75); color: #fff; cursor: pointer; }
.nav-left { left: 12px; }
.nav-right { right: 12px; }
.photo-count { position: absolute; right: 12px; bottom: 12px; display: flex; align-items: center; gap: 5px; padding: 5px 10px; border-radius: 20px; background: rgba(13, 27, 42, .75); color: #fff; font-size: 11px; font-weight: 700; }
.thumbnail-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); grid-template-rows: repeat(2, minmax(0, 1fr)); gap: 10px; min-width: 0; }
.thumbnail { position: relative; overflow: hidden; padding: 0; border: 2px solid transparent; border-radius: 11px; background: #eef2f7; cursor: pointer; }
.thumbnail-active { border-color: #d4af37; }
.thumbnail-more { position: absolute; inset: 0; display: grid; place-items: center; background: rgba(13, 27, 42, .72); color: #fff; font-size: 22px; font-weight: 800; }
.info-section { padding: 34px 20px 70px; }
.info-inner { display: grid; grid-template-columns: minmax(0, 1fr) 300px; align-items: start; gap: 30px; }
.title-block { margin-bottom: 22px; }
.category-label { margin-bottom: 7px; color: #c69e26; font-size: 10px; font-weight: 800; letter-spacing: 1px; text-transform: uppercase; }
.listing-title { margin: 0 0 9px; color: #0d1b2a; font-size: 27px; font-weight: 800; line-height: 1.25; }
.location-row { display: flex; align-items: center; gap: 4px; margin-bottom: 19px; color: #64748b; font-size: 13px; }
.location-row .q-icon { color: #94a3b8; }
.price-label { margin-bottom: 4px; color: #94a3b8; font-size: 10px; font-weight: 800; letter-spacing: .8px; text-transform: uppercase; }
.price-value { color: #0d1b2a; font-size: 28px; font-weight: 800; letter-spacing: -.7px; line-height: 1.2; }
.section-divider { height: 1px; margin: 23px 0; background: #e8ecf4; }
.block-title { margin: 0 0 15px; color: #0d1b2a; font-size: 14px; font-weight: 800; }
.feature-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; }
.feature-card { min-width: 0; padding: 13px 8px; border: 1px solid #e8ecf4; border-radius: 12px; background: #fff; text-align: center; }
.feature-icon { display: grid; width: 36px; height: 36px; margin: 0 auto 8px; place-items: center; border-radius: 11px; background: #f1f5f9; color: #0d1b2a; }
.feature-value { overflow-wrap: anywhere; color: #0d1b2a; font-size: 17px; font-weight: 800; line-height: 1.2; }
.feature-label { margin-top: 4px; color: #94a3b8; font-size: 9px; font-weight: 800; letter-spacing: .4px; text-transform: uppercase; }
.description { margin: 0; color: #475569; font-size: 14px; line-height: 1.85; white-space: pre-line; }
.unit-list { display: grid; gap: 10px; }
.unit-card { padding: 14px; border: 1px solid #e8ecf4; border-radius: 12px; background: #fff; }
.unit-heading { display: flex; align-items: center; justify-content: space-between; gap: 10px; color: #0d1b2a; }
.unit-heading span { color: #0d1b2a; font-weight: 800; }
.unit-heading small { color: #94a3b8; font-size: 10px; font-weight: 600; }
.unit-specs { display: flex; flex-wrap: wrap; gap: 10px 16px; margin-top: 11px; color: #64748b; font-size: 11px; }
.unit-specs span { display: inline-flex; align-items: center; gap: 4px; }
.unit-description, .rules-text { margin: 10px 0 0; color: #64748b; font-size: 12px; line-height: 1.65; }
.amenity-block { margin-top: 22px; }
.amenity-list { display: flex; flex-wrap: wrap; gap: 8px; }
.amenity-chip { display: inline-flex; align-items: center; gap: 5px; padding: 6px 9px; border-radius: 18px; background: #fff; color: #475569; font-size: 11px; }
.amenity-chip .q-icon { color: #16a34a; }
.muted { color: #94a3b8; font-size: 12px; }
.contact-card { position: sticky; top: 24px; padding: 20px; border: 1px solid #e8ecf4; border-radius: 17px; background: #fff; box-shadow: 0 4px 20px rgba(13, 27, 42, .06); }
.agent-row { display: flex; align-items: center; gap: 11px; }
.agent-avatar { width: 50px; height: 50px; flex: 0 0 50px; overflow: hidden; border: 2px solid #e8ecf4; border-radius: 50%; background: #fff; }
.agent-avatar img { width: 100%; height: 100%; object-fit: cover; }
.agent-name { color: #0d1b2a; font-size: 13px; font-weight: 800; }
.agent-role { margin-top: 3px; color: #94a3b8; font-size: 10px; }
.card-divider { height: 1px; margin: 16px 0; background: #edf0f5; }
.contact-actions { display: grid; gap: 8px; }
.contact-button { display: flex; align-items: center; justify-content: center; gap: 8px; min-height: 38px; border-radius: 9px; font-size: 12px; font-weight: 700; text-decoration: none; }
.button-whatsapp { background: #f0fdf4; color: #15803d; }
.button-call { background: #f1f5f9; color: #0d1b2a; }
.contact-unavailable { padding: 3px 0; }
.meta-row { display: flex; justify-content: space-between; gap: 12px; padding: 8px 0; border-bottom: 1px solid #f1f5f9; font-size: 11px; }
.meta-key { color: #94a3b8; }
.meta-value { color: #0d1b2a; font-weight: 700; text-align: right; overflow-wrap: anywhere; }
.sidebar-button { width: 100%; border-radius: 9px; font-size: 11px; font-weight: 800; }
.button-edit { background: #0d1b2a; color: #d4af37; }
.button-submit, .button-approve { background: #15803d; color: #fff; }
.button-reject, .button-deactivate { background: #dc2626; color: #fff; }
.status-line { display: flex; align-items: center; justify-content: space-between; gap: 8px; color: #64748b; font-size: 11px; }
.status-badge { padding: 4px 8px; border-radius: 10px; color: #fff; font-size: 9px; font-weight: 800; }
.status-published, .status-active { background: #15803d; }
.status-pending_review { background: #d97706; }
.status-draft, .status-inactive, .status-expired { background: #64748b; }
.status-rejected { background: #dc2626; }
.rejection-note { display: flex; align-items: flex-start; gap: 5px; margin-top: 10px; padding: 8px; border-radius: 8px; background: #fef2f2; color: #b91c1c; font-size: 10px; line-height: 1.5; }
@media (max-width: 850px) { .info-inner { grid-template-columns: minmax(0, 1fr) 270px; gap: 20px; } .gallery-grid { height: 300px; } }
@media (max-width: 650px) { .gallery-section { padding: 14px 12px; } .gallery-grid { grid-template-columns: 1fr; height: auto; } .main-image-wrap { min-height: 230px; } .thumbnail-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); grid-template-rows: 78px; } .info-section { padding: 24px 12px 45px; } .info-inner { grid-template-columns: 1fr; } .contact-card { position: static; } .feature-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
</style>
